import {
  missingSetupPrerequisites,
  setupPrerequisiteCopy,
  setupPrerequisiteOrder,
  setupPrerequisites,
  voiceCapability,
  type SetupPrerequisite,
  type SetupPrerequisiteStatuses,
  type SetupSettingsSection,
} from './communications-setup-presentation'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft,
  CheckCircle2,
  PhoneCall,
  ShieldAlert,
  ShieldCheck,
} from 'lucide-react'
import { useNavigate, useParams } from 'react-router-dom'

import { Button } from '../../ui/Button/Button'
import { Spinner } from '../../ui/Spinner/Spinner'
import { StatePanel } from '../../ui/StatePanel/StatePanel'
import { useAuth } from '../auth/AuthProvider'
import { useTenant } from '../tenant/TenantProvider'
import { createSettingsRepository } from '../settings/settings-repository'
import type { SettingsRepository } from '../settings/settings-repository'
import { normalizeClinicMainPhone } from '../settings/settings-contracts'
import { getOnboardingApiClient } from './onboarding-api'
import type { OnboardingApiClient } from './onboarding-api'
import styles from './OrganizationCommunicationsSetupPage.module.css'

type Capability = 'TEXT_ONLY' | 'VOICE_ONLY' | 'VOICE_AND_TEXT'

const autoAdvanceProgress = new Set([
  'WAITING_FOR_NUMBER',
  'CONFIGURING_TWILIO',
  'CONFIGURING_VAPI_ASSISTANT',
  'CONFIGURING_VAPI_DESTINATION',
  'PROJECTING_SMS_RUNTIME',
  'PROJECTING_VOICE_RUNTIME',
  'READY_FOR_VALIDATION',
])

const capabilityCopy: Record<Capability, { title: string; copy: string }> = {
  TEXT_ONLY: {
    title: 'Text only',
    copy: 'Native inbound and outbound texting.',
  },
  VOICE_ONLY: {
    title: 'Voice only',
    copy: 'Native AI voice with front-desk forwarding.',
  },
  VOICE_AND_TEXT: {
    title: 'Voice + Text',
    copy: 'Native calling and texting on one primary number.',
  },
}

export function OrganizationCommunicationsSetupPage({
  apiClient,
  settingsRepository,
}: {
  apiClient?: OnboardingApiClient
  settingsRepository?: SettingsRepository
} = {}) {
  const { tenantUid = '' } = useParams()
  const navigate = useNavigate()
  const tenant = useTenant()
  const auth = useAuth()
  const queryClient = useQueryClient()
  const user = auth.user
  const api = apiClient ?? getOnboardingApiClient()
  const settings = useMemo(
    () =>
      settingsRepository ?? createSettingsRepository({ getUser: () => user }),
    [settingsRepository, user]
  )
  const [mainPhone, setMainPhone] = useState('')
  const [publicNumber, setPublicNumber] = useState('')
  const [capability, setCapability] = useState<Capability>('VOICE_AND_TEXT')
  const [confirmedSensitiveChange, setConfirmedSensitiveChange] =
    useState(false)
  const [recoveryCompleted, setRecoveryCompleted] = useState(false)
  const [autoAdvanceFailure, setAutoAdvanceFailure] = useState<{
    key: string
    error: Error
  } | null>(null)
  const lastAutoAdvanceKey = useRef<string | null>(null)
  const autoAdvanceAttempts = useRef(new Map<string, number>())

  const profile = useQuery({
    queryKey: ['organization-communications-profile', tenantUid],
    queryFn: () => settings.getClinicProfile(tenantUid),
    enabled: Boolean(user && tenantUid),
  })
  const communications = useQuery({
    queryKey: ['organization-communications', tenantUid],
    queryFn: () => api.getOrganizationCommunications!(user!, tenantUid),
    enabled: Boolean(user && tenantUid),
    refetchInterval: (query) =>
      !recoveryCompleted &&
      !query.state.data?.recovery &&
      ['PROVISIONING', 'VALIDATING'].includes(query.state.data?.status ?? '')
        ? 3000
        : false,
  })

  const prerequisites = useQuery({
    queryKey: ['organization-communications-prerequisites', tenantUid],
    queryFn: () =>
      api.getOrganizationCommunicationsPrerequisites!(user!, tenantUid),
    enabled: Boolean(
      user && tenantUid && api.getOrganizationCommunicationsPrerequisites
    ),
    retry: false,
  })
  // Unavailable statuses (an older service) leave the server-enforced flow as
  // it was; the setup and advance gates still refuse with a precise reason.
  const prerequisiteStatuses = prerequisites.data?.prerequisites ?? null

  const twilio = useQuery({
    queryKey: ['organization-twilio', tenantUid, user?.uid ?? null],
    queryFn: () => api.verifyOrganizationTwilio!(user!, tenantUid),
    enabled: Boolean(user && tenantUid && api.verifyOrganizationTwilio),
    retry: false,
  })
  const communicationsState = communications.data
  // Settings can change during setup. The server re-checks before every
  // provisioning step; the page pauses instead of retrying into that refusal.
  const pausedPrerequisites =
    communicationsState &&
    prerequisiteStatuses &&
    (communicationsState.status === 'PROVISIONING' ||
      (communicationsState.status === 'VALIDATING' &&
        communicationsState.readiness.text !== 'READY' &&
        communicationsState.readiness.voice !== 'READY'))
      ? missingSetupPrerequisites(
          prerequisiteStatuses,
          voiceCapability(communicationsState.capability)
        )
      : []
  const voiceBlocked = prerequisiteStatuses
    ? missingSetupPrerequisites(prerequisiteStatuses, true).some(
        (key) => setupPrerequisites[key].voiceOnly
      )
    : false
  const autoAdvanceEligibilityKey =
    !recoveryCompleted &&
    communicationsState &&
    !communicationsState.recovery &&
    pausedPrerequisites.length === 0 &&
    ['PROVISIONING', 'VALIDATING'].includes(communicationsState.status) &&
    communicationsState.progress &&
    autoAdvanceProgress.has(communicationsState.progress)
      ? `${tenantUid}:${communicationsState.revision}:${communicationsState.status}:${communicationsState.progress}`
      : null
  const communicationsDataUpdatedAt = communications.dataUpdatedAt
  const refetchCommunications = communications.refetch

  useEffect(() => {
    if (profile.data && !mainPhone) {
      setMainPhone(profile.data.clinicInfo.dentistryPhone)
    }
  }, [mainPhone, profile.data])

  const {
    mutate: advanceCommunications,
    isPending: advanceIsPending,
    isError: advanceIsError,
  } = useMutation({
    mutationFn: () => api.advanceOrganizationCommunications!(user!, tenantUid),
    onSuccess: (result) => {
      queryClient.setQueryData(
        ['organization-communications', tenantUid],
        result
      )
      void queryClient.invalidateQueries({ queryKey: ['organization-admin'] })
      void queryClient.invalidateQueries({
        queryKey: ['organization-twilio', tenantUid],
      })
    },
  })

  useEffect(() => {
    if (!user || !autoAdvanceEligibilityKey) return
    const advanceKey = autoAdvanceEligibilityKey
    const attempts = autoAdvanceAttempts.current.get(advanceKey) ?? 0
    if (lastAutoAdvanceKey.current === advanceKey || attempts >= 3) return
    lastAutoAdvanceKey.current = advanceKey
    autoAdvanceAttempts.current.set(advanceKey, attempts + 1)
    void api.advanceOrganizationCommunications!(user, tenantUid)
      .then((result) => {
        setAutoAdvanceFailure(null)
        queryClient.setQueryData(
          ['organization-communications', tenantUid],
          result
        )
        void queryClient.invalidateQueries({ queryKey: ['organization-admin'] })
        void queryClient.invalidateQueries({
          queryKey: ['organization-twilio', tenantUid],
        })
      })
      .catch((error: unknown) => {
        if (lastAutoAdvanceKey.current === advanceKey) {
          lastAutoAdvanceKey.current = null
        }
        void queryClient.invalidateQueries({
          queryKey: ['organization-communications-prerequisites', tenantUid],
        })
        if (attempts + 1 >= 3) {
          setAutoAdvanceFailure({
            key: advanceKey,
            error:
              error instanceof Error
                ? error
                : new Error('Communications setup could not continue safely.'),
          })
        }
        void refetchCommunications()
      })
  }, [
    api,
    autoAdvanceEligibilityKey,
    communicationsDataUpdatedAt,
    queryClient,
    refetchCommunications,
    tenantUid,
    user,
  ])

  const repair = useMutation({
    mutationFn: () => api.repairOrganizationTwilio!(user!, tenantUid),
    onSuccess: (result) => {
      queryClient.setQueryData(
        ['organization-twilio', tenantUid, user?.uid ?? null],
        result
      )
    },
    onError: () => {
      void twilio.refetch()
    },
  })

  const recovery = useMutation({
    mutationFn: () => {
      const intent = communications.data?.recovery
      if (!intent) throw new Error('Recovery is no longer available.')
      return api.recoverOrganizationCommunications!(user!, {
        tenantUid,
        planId: intent.planId,
      })
    },
    onSuccess: () => {
      setRecoveryCompleted(true)
      void queryClient.invalidateQueries({ queryKey: ['organization-admin'] })
      void queryClient.invalidateQueries({
        queryKey: ['organization-twilio', tenantUid],
      })
    },
  })

  const setup = useMutation({
    mutationFn: async () => {
      const normalized = normalizeClinicMainPhone(mainPhone)
      if (!normalized || !profile.data || !communications.data) {
        throw new Error('Enter a valid Main phone and Twilio number.')
      }
      const existing = normalizeClinicMainPhone(
        profile.data.clinicInfo.dentistryPhone
      )
      if (existing && existing !== normalized && !confirmedSensitiveChange) {
        throw new Error(
          'Confirm the sensitive Main phone change before continuing.'
        )
      }
      if (existing !== normalized) {
        await settings.updateClinicInfo(tenantUid, {
          ...profile.data.clinicInfo,
          dentistryPhone: normalized,
        })
      }
      return api.setupOrganizationCommunications!(user!, {
        tenantUid,
        expectedRevision: communications.data.revision,
        publicNumber,
        capability,
      })
    },
    onSuccess: (result) => {
      queryClient.setQueryData(
        ['organization-communications', tenantUid],
        result
      )
      void profile.refetch()
      void queryClient.invalidateQueries({ queryKey: ['organization-admin'] })
      void queryClient.invalidateQueries({
        queryKey: ['organization-twilio', tenantUid],
      })
    },
    onError: () => {
      // A canonical step may have committed before an external-provider
      // response was lost. Always re-read lifecycle ownership before retrying.
      void communications.refetch()
      void profile.refetch()
      void queryClient.invalidateQueries({
        queryKey: ['organization-communications-prerequisites', tenantUid],
      })
    },
  })

  if (profile.isPending || communications.isPending) {
    return (
      <div className={styles.loading}>
        <Spinner label="Loading Communications setup" />
      </div>
    )
  }
  if (!profile.data || !communications.data) {
    return (
      <StatePanel
        title="Communications unavailable"
        description="This clinic setup could not be loaded safely."
      />
    )
  }

  const state = communications.data
  const canOpenSettings = tenant.authorizedClinics.some(
    (clinic) => clinic.id === tenantUid
  )
  const openSettings = (section: SetupSettingsSection) => {
    if (tenant.selectClinic(tenantUid)) {
      void navigate(
        `/settings?section=${section}&communicationsSetup=${encodeURIComponent(tenantUid)}`
      )
    }
  }
  const changedMainPhone = Boolean(
    normalizeClinicMainPhone(profile.data.clinicInfo.dentistryPhone) &&
    normalizeClinicMainPhone(profile.data.clinicInfo.dentistryPhone) !==
      normalizeClinicMainPhone(mainPhone)
  )

  return (
    <main className={styles.page}>
      <button
        className={styles.back}
        type="button"
        onClick={() => void navigate('/settings?section=organization')}
      >
        <ArrowLeft size={16} aria-hidden="true" /> Organization settings
      </button>
      <header className={styles.hero}>
        <span>
          <PhoneCall size={22} aria-hidden="true" />
        </span>
        <div>
          <p>Organization Communications</p>
          <h1>Connect Twilio number</h1>
          <p>{profile.data.clinicInfo.dentistryName}</p>
        </div>
      </header>

      {recoveryCompleted ? (
        <section className={styles.recoverySuccess} aria-live="polite">
          <CheckCircle2 size={30} aria-hidden="true" />
          <div>
            <h2>Previous phone routing restored</h2>
            <p>
              Communications setup is paused. The existing plan remains safe to
              resume later.
            </p>
          </div>
        </section>
      ) : state.recovery ? (
        <section className={styles.recovery} role="alert">
          <ShieldAlert size={30} aria-hidden="true" />
          <div>
            <h2>Communications setup needs recovery</h2>
            <p>
              Flio will restore the previous phone routing and pause this setup.
              The existing plan will remain available to resume later.
            </p>
            {recovery.isError ? (
              <p className={styles.error} role="alert">
                {recovery.error instanceof Error
                  ? recovery.error.message
                  : 'Recovery could not be completed safely.'}
              </p>
            ) : null}
            <Button
              type="button"
              variant="secondary"
              disabled={recovery.isPending}
              onClick={() => {
                if (
                  window.confirm(
                    'Restore the previous phone routing and pause this communications setup?'
                  )
                ) {
                  recovery.mutate()
                }
              }}
            >
              {recovery.isPending
                ? 'Recovering communications setup…'
                : 'Recover communications setup'}
            </Button>
          </div>
        </section>
      ) : state.status === 'READY' ? (
        <section className={styles.ready}>
          <CheckCircle2 size={30} aria-hidden="true" />
          <div>
            <h2>
              {twilio.data?.tenantUid === tenantUid &&
              twilio.data.status === 'CONNECTED'
                ? 'Twilio connected'
                : twilio.data?.tenantUid === tenantUid &&
                    twilio.data.status === 'NEEDS_REPAIR'
                  ? 'Twilio connection needs repair'
                  : 'Twilio verification required'}
            </h2>
            <p>
              Assigned Twilio number: {state.publicNumber}. Canonical activation
              alone does not verify the current provider connection.
            </p>
            {twilio.data?.tenantUid === tenantUid &&
            twilio.data.status === 'NEEDS_REPAIR' ? (
              <Button
                type="button"
                disabled={repair.isPending}
                onClick={() => {
                  if (
                    window.confirm(
                      `Repair Twilio routing for ${profile.data.clinicInfo.dentistryName} using its assigned number ${state.publicNumber}? This does not change the clinic's front-desk phone or purchase a number.`
                    )
                  )
                    repair.mutate()
                }}
              >
                {repair.isPending ? 'Repairing…' : 'Repair Twilio connection'}
              </Button>
            ) : null}
            {repair.isError ? (
              <p role="alert">
                Repair could not be verified. Check the connection before
                retrying.
              </p>
            ) : null}
            <Button
              type="button"
              variant="secondary"
              disabled={twilio.isFetching}
              onClick={() => void twilio.refetch()}
            >
              Verify Twilio connection
            </Button>
          </div>
        </section>
      ) : (state.status === 'PARTIAL' || state.status === 'BLOCKED') &&
        state.publicNumber ? (
        <section className={styles.progress} role="alert">
          <ShieldCheck size={28} aria-hidden="true" />
          <div>
            <h2>
              {state.status === 'PARTIAL'
                ? 'Communications partially ready'
                : 'Setup needs attention'}
            </h2>
            <p>
              {state.reason ??
                'Canonical provider evidence requires safe reconciliation.'}
            </p>
            <Button
              type="button"
              variant="secondary"
              disabled={advanceIsPending}
              onClick={() => advanceCommunications()}
            >
              {advanceIsPending ? 'Reconciling…' : 'Retry reconciliation'}
            </Button>
          </div>
        </section>
      ) : pausedPrerequisites.length > 0 && prerequisiteStatuses ? (
        <section className={styles.progress} role="alert">
          <ShieldAlert size={28} aria-hidden="true" />
          <div>
            <h2>Setup paused until clinic settings are complete</h2>
            <p>
              Clinic settings changed after setup started. Nothing more is
              provisioned until these are complete; setup then continues where
              it stopped.
            </p>
            <SetupPrerequisiteChecklist
              statuses={prerequisiteStatuses}
              keys={pausedPrerequisites}
              voice={voiceCapability(state.capability)}
              canOpen={canOpenSettings}
              onOpen={openSettings}
            />
            <Button
              type="button"
              variant="secondary"
              disabled={prerequisites.isFetching}
              onClick={() => void prerequisites.refetch()}
            >
              Check again
            </Button>
          </div>
        </section>
      ) : autoAdvanceFailure?.key === autoAdvanceEligibilityKey ? (
        <section className={styles.progress} role="alert">
          <ShieldAlert size={28} aria-hidden="true" />
          <div>
            <h2>Communications setup needs attention</h2>
            <p>{autoAdvanceFailure.error.message}</p>
          </div>
        </section>
      ) : state.progress === 'RETRYABLE_FAILURE' ? (
        <section className={styles.progress} role="alert">
          <ShieldAlert size={28} aria-hidden="true" />
          <div>
            <h2>Provisioning securely — retryable failure</h2>
            <p>
              {state.canRetry
                ? 'The previous attempt ended before provider dispatch. Retry the canonical setup command.'
                : 'The bounded retry slot is unavailable. Flio support must review the canonical command.'}
            </p>
            {advanceIsError ? (
              <p className={styles.error}>
                Retry could not be completed safely. The canonical setup state
                was preserved.
              </p>
            ) : null}
            {state.canRetry ? (
              <Button
                type="button"
                variant="secondary"
                disabled={advanceIsPending}
                onClick={() => advanceCommunications()}
              >
                {advanceIsPending ? 'Retrying…' : 'Retry Communications setup'}
              </Button>
            ) : null}
          </div>
        </section>
      ) : state.status === 'PROVISIONING' || state.status === 'VALIDATING' ? (
        <section className={styles.progress} aria-live="polite">
          <Spinner label="Provisioning Communications" />
          <div>
            <h2>
              {state.status === 'VALIDATING'
                ? 'Validating setup'
                : 'Provisioning securely'}
            </h2>
            <p>
              {state.progress?.toLowerCase().replaceAll('_', ' ') ??
                'Checking canonical provider evidence'}
              .
            </p>
          </div>
        </section>
      ) : state.status === 'SETUP_REQUIRED' ? (
        <section className={styles.progress} role="alert">
          <ShieldCheck size={28} aria-hidden="true" />
          <div>
            <h2>Complete clinic setup first</h2>
            <p>{setupPrerequisiteCopy(state.reason)}</p>
            {prerequisiteStatuses ? (
              <SetupPrerequisiteChecklist
                statuses={prerequisiteStatuses}
                voice={null}
                canOpen={canOpenSettings}
                onOpen={openSettings}
              />
            ) : [
                'CLINIC_INFO_AND_WORKING_HOURS_REQUIRED',
                'CLINIC_INFO_REQUIRED',
                'WORKING_HOURS_REQUIRED',
              ].includes(state.reason ?? '') ? (
              <ul aria-label="Setup prerequisites">
                <li>
                  Clinic Info —{' '}
                  {state.reason === 'WORKING_HOURS_REQUIRED'
                    ? 'Complete'
                    : 'Required'}
                </li>
                <li>
                  Working Hours —{' '}
                  {state.reason === 'CLINIC_INFO_REQUIRED'
                    ? 'Complete'
                    : 'Required'}
                </li>
              </ul>
            ) : null}
            <p>
              Save the required settings, then use Continue to phone &amp;
              texting to return here.
            </p>
            {prerequisiteStatuses ? null : (
              <Button
                type="button"
                variant="secondary"
                disabled={!canOpenSettings}
                onClick={() =>
                  openSettings(
                    state.reason === 'WORKING_HOURS_REQUIRED'
                      ? 'hours'
                      : 'clinic'
                  )
                }
              >
                Open clinic settings
              </Button>
            )}
          </div>
        </section>
      ) : (
        <form
          className={styles.form}
          onSubmit={(event) => {
            event.preventDefault()
            setup.mutate()
          }}
        >
          <section className={styles.card}>
            <div className={styles.cardTitle}>
              <ShieldCheck size={18} aria-hidden="true" />
              <div>
                <h2>Clinic Main phone</h2>
                <p>
                  Front-desk contact and forwarding destination. This is not the
                  dedicated Twilio communications number.
                </p>
              </div>
            </div>
            <label>
              Main phone
              <input
                type="tel"
                value={mainPhone}
                onChange={(event) => {
                  setMainPhone(event.target.value)
                  setConfirmedSensitiveChange(false)
                }}
                required
              />
            </label>
            {changedMainPhone ? (
              <label className={styles.confirm}>
                <input
                  type="checkbox"
                  checked={confirmedSensitiveChange}
                  onChange={(event) =>
                    setConfirmedSensitiveChange(event.target.checked)
                  }
                />
                I confirm this sensitive routing destination change.
              </label>
            ) : null}
          </section>
          <section className={styles.card}>
            <div className={styles.cardTitle}>
              <PhoneCall size={18} aria-hidden="true" />
              <div>
                <h2>Flio/Twilio number</h2>
                <p>
                  Enter one existing number controlled by the reviewed Flio
                  Twilio account.
                </p>
              </div>
            </div>
            <label>
              Primary number
              <input
                type="tel"
                value={publicNumber}
                onChange={(event) => setPublicNumber(event.target.value)}
                placeholder="+1 555 555 0123"
                required
              />
            </label>
            <p className={styles.helper}>
              One canonical primary number is supported in this version. Flio
              verifies account control before assignment.
            </p>
          </section>
          <fieldset className={styles.card}>
            <legend>Capabilities</legend>
            {voiceBlocked && prerequisiteStatuses ? (
              <div className={styles.voicePrerequisites} role="note">
                <p>
                  <strong>Voice requires Services and Booking.</strong> Complete
                  them in Settings, or set up Text only now.
                </p>
                <SetupPrerequisiteChecklist
                  statuses={prerequisiteStatuses}
                  keys={missingSetupPrerequisites(
                    prerequisiteStatuses,
                    true
                  ).filter((key) => setupPrerequisites[key].voiceOnly)}
                  voice
                  canOpen={canOpenSettings}
                  onOpen={openSettings}
                />
              </div>
            ) : null}
            <div className={styles.options}>
              {(Object.keys(capabilityCopy) as Capability[]).map((value) => (
                <label className={styles.option} key={value}>
                  <input
                    type="radio"
                    name="capability"
                    checked={capability === value}
                    disabled={voiceBlocked && value !== 'TEXT_ONLY'}
                    onChange={() => setCapability(value)}
                  />
                  <span>
                    <strong>{capabilityCopy[value].title}</strong>
                    <small>{capabilityCopy[value].copy}</small>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
          {voiceBlocked && capability !== 'TEXT_ONLY' ? (
            <p className={styles.helper}>
              Choose Text only, or complete Services and Booking to set up
              Voice.
            </p>
          ) : null}
          {state.status === 'BLOCKED' || setup.isError || advanceIsError ? (
            <p className={styles.error} role="alert">
              {state.reason ??
                (setup.error instanceof Error
                  ? setup.error.message
                  : 'Setup needs attention. Retry safely after resolving the issue.')}
            </p>
          ) : null}
          <div className={styles.actions}>
            <Button
              type="button"
              variant="secondary"
              onClick={() => void navigate('/settings?section=organization')}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                setup.isPending || (voiceBlocked && capability !== 'TEXT_ONLY')
              }
            >
              {setup.isPending ? 'Verifying…' : 'Confirm and provision'}
            </Button>
          </div>
        </form>
      )}
    </main>
  )
}

function SetupPrerequisiteChecklist({
  statuses,
  keys,
  voice,
  canOpen,
  onOpen,
}: {
  statuses: SetupPrerequisiteStatuses
  keys?: SetupPrerequisite[]
  // null: no capability chosen yet, so Voice-only items are labeled as such.
  voice: boolean | null
  canOpen: boolean
  onOpen: (section: SetupSettingsSection) => void
}) {
  const shown = (keys ?? setupPrerequisiteOrder).filter(
    (key) =>
      statuses[key] !== 'NOT_APPLICABLE' &&
      (voice !== false || !setupPrerequisites[key].voiceOnly)
  )
  return (
    <ul className={styles.prerequisites} aria-label="Setup prerequisites">
      {shown.map((key) => {
        const item = setupPrerequisites[key]
        const required = statuses[key] === 'REQUIRED'
        return (
          <li key={key}>
            <span>
              <strong>
                {item.label}
                {item.voiceOnly && voice === null ? ' (Voice)' : ''}
              </strong>{' '}
              — {required ? 'Required' : 'Complete'}
              <small>{item.detail}</small>
            </span>
            {required ? (
              <Button
                type="button"
                variant="secondary"
                disabled={!canOpen}
                onClick={() => onOpen(item.section)}
              >
                Open {item.label}
              </Button>
            ) : null}
          </li>
        )
      })}
    </ul>
  )
}
